import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Modal } from "antd";
import moment from "moment";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getUserList, getUserInfo } from "redux/selectors";
import { find } from "lodash";
import COLORS from "common/Colors";

const StyledCelebrationModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 22px;
    overflow: hidden;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-close {
    top: 10px;
    right: 10px;
    z-index: 3;
  }
  .ant-modal-close-x {
    color: #fff;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
  }
`;

// Every celebration type shares the app's own brand gradient so the popup
// feels native instead of an unrelated color scheme bolted on.
const BRAND_GRADIENT = `linear-gradient(145deg, ${COLORS.PRIMARY} 0%, ${COLORS.SELECTED} 100%)`;
const CONFETTI_COLORS = [
  COLORS.WHITE,
  COLORS.WARNING,
  COLORS.SUCCESS,
  COLORS.SECONDARY,
  "#ffffffaa",
];

const CARD_THEME = {
  birthday: { emoji: "🎂", heading: "Happy Birthday!" },
  anniversary: { emoji: "🏆", heading: "Happy Work Anniversary!" },
  welcome: { emoji: "👋", heading: "Welcome to the Team!" },
};

const fall = keyframes`
  0% { transform: translateY(-10%) rotate(0deg); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translateY(320%) rotate(360deg); opacity: 0; }
`;

const pop = keyframes`
  0% { transform: scale(0) rotate(-25deg); opacity: 0; }
  60% { transform: scale(1.25) rotate(8deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const fadeInUp = keyframes`
  from { transform: translateY(16px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const CardWrap = styled.div`
  position: relative;
  background: ${BRAND_GRADIENT};
  padding: 40px 28px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;

const ConfettiPiece = styled.span`
  position: absolute;
  top: -10%;
  left: ${(props) => props.left}%;
  width: ${(props) => props.size}px;
  height: ${(props) => props.size * 2.6}px;
  background: ${(props) => props.color};
  border-radius: 2px;
  animation: ${fall} ${(props) => props.duration}s linear infinite;
  animation-delay: ${(props) => props.delay}s;
  pointer-events: none;
`;

const Popper = styled.span`
  position: absolute;
  top: 14px;
  ${(props) => (props.side === "left" ? "left: 18px;" : "right: 18px;")}
  font-size: 26px;
  transform-origin: bottom center;
  animation: ${pop} 0.6s ease-out;
  ${(props) => props.side === "right" && "transform: scaleX(-1);"}
`;

const GlassCard = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 28px 20px 24px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.32);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  color: #fff;
  animation: ${fadeInUp} 0.45s ease-out;
`;

const EmojiRow = styled.div`
  font-size: 26px;
  letter-spacing: 10px;
  margin-bottom: 12px;
`;

const StyledAvatar = styled(Avatar)`
  border: 3px solid #fff;
  background: #fff;
  color: ${COLORS.PRIMARY};
  font-weight: 700;
  font-size: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  flex-shrink: 0;
`;

const Name = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin-top: 14px;
`;

const Heading = styled.div`
  font-size: 15px;
  font-weight: 600;
  margin-top: 2px;
  opacity: 0.95;
`;

const SubText = styled.div`
  font-size: 13px;
  opacity: 0.9;
  margin-top: 8px;
`;

const YearsBadge = styled.div`
  margin-top: 12px;
  padding: 5px 16px;
  border-radius: 999px;
  background: ${COLORS.WARNING};
  color: ${COLORS.BLACK};
  font-weight: 700;
  font-size: 13px;
`;

const Dots = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 20px;
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) =>
    props.active ? "#fff" : "rgba(255, 255, 255, 0.4)"};
  transition: background 0.2s ease;
`;

const NextButton = styled.button`
  margin-top: 16px;
  padding: 7px 20px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join("");

const useConfetti = (count = 16) =>
  useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        key: i,
        left: Math.round(Math.random() * 96),
        size: 5 + Math.round(Math.random() * 4),
        duration: 2.6 + Math.random() * 2,
        delay: Math.random() * 2.5,
        color:
          CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

// Matches the logged-in user (userInfo.userid) against the already-loaded
// userList to read their own dateofbirth/startdate — no extra API calls.
function CelebrationModal() {
  const { t } = useTranslation();
  const userList = useSelector(getUserList);
  const userInfo = useSelector(getUserInfo);
  const confetti = useConfetti();

  const [visible, setVisible] = useState(false);
  const [celebrations, setCelebrations] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!userInfo?.userid || !userList?.length) return;

    const today = moment();
    const dedupKey = `celebration_shown_${userInfo.userid}_${today.format(
      "YYYY-MM-DD"
    )}`;
    if (window.localStorage.getItem(dedupKey)) return;
    window.localStorage.setItem(dedupKey, "true");

    const currentUser = find(userList, { userid: userInfo.userid });
    if (!currentUser) return;

    const found = [];

    const dob = currentUser.dateofbirth
      ? moment(currentUser.dateofbirth)
      : null;
    if (
      dob?.isValid() &&
      dob.date() === today.date() &&
      dob.month() === today.month()
    ) {
      found.push({ type: "birthday", user: currentUser });
    }

    const startDate = currentUser.hireddate
      ? moment(currentUser.hireddate)
      : null;
    if (
      startDate?.isValid() &&
      startDate.date() === today.date() &&
      startDate.month() === today.month()
    ) {
      if (startDate.year() === today.year()) {
        found.push({ type: "welcome", user: currentUser });
      } else if (startDate.year() < today.year()) {
        found.push({
          type: "anniversary",
          user: currentUser,
          yearsCompleted: today.year() - startDate.year(),
        });
      }
    }

    if (found.length) {
      setCelebrations(found);
      setCurrentIndex(0);
      setVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.userid, userList]);

  if (!celebrations.length) return null;

  const current = celebrations[currentIndex];
  const theme = CARD_THEME[current.type];
  const hasNext = currentIndex < celebrations.length - 1;

  return (
    <StyledCelebrationModal
      visible={visible}
      centered
      closable
      footer={null}
      width={420}
      onCancel={() => setVisible(false)}
    >
      <CardWrap>
        {confetti.map((piece) => (
          <ConfettiPiece
            key={piece.key}
            left={piece.left}
            size={piece.size}
            duration={piece.duration}
            delay={piece.delay}
            color={piece.color}
          />
        ))}
        <Popper side="left">🎉</Popper>
        <Popper side="right">🎉</Popper>

        <GlassCard key={current.type}>
          <EmojiRow>{theme.emoji}</EmojiRow>
          <StyledAvatar src={current.user.profileurl || undefined} size={76}>
            {getInitials(current.user.fullname)}
          </StyledAvatar>
          <Name>{current.user.fullname}</Name>
          <Heading>{t(theme.heading)}</Heading>
          {current.type === "birthday" && (
            <SubText>{t("Wishing you a fantastic year ahead!")}</SubText>
          )}
          {current.type === "welcome" && (
            <SubText>{t("We're excited to have you on the team!")}</SubText>
          )}
          {current.type === "anniversary" && (
            <YearsBadge>
              {current.yearsCompleted}{" "}
              {current.yearsCompleted === 1 ? t("Year") : t("Years")}{" "}
              {t("at the company")}
            </YearsBadge>
          )}

          {celebrations.length > 1 && (
            <Dots>
              {celebrations.map((c, i) => (
                <Dot key={c.type} active={i === currentIndex} />
              ))}
            </Dots>
          )}
          {hasNext && (
            <NextButton
              type="button"
              onClick={() => setCurrentIndex((i) => i + 1)}
            >
              {t("Next")} →
            </NextButton>
          )}
        </GlassCard>
      </CardWrap>
    </StyledCelebrationModal>
  );
}

export default CelebrationModal;
